import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Plus, Clock, Users, MessageSquare, BookOpen, Loader2, Trash2,
  Calendar, X, ChevronRight, Sparkles, Heart,
} from "lucide-react";
import { lifeStoryApi, timelineApi, peopleApi, interviewApi } from "../api/life_story";

const MODE_LABELS = {
  biography: "Biography",
  memoir: "Memoir",
  autobiography: "Autobiography",
  family_history: "Family History",
  legacy_project: "Legacy Project",
};

export default function LifeStoryStudio() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [events, setEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activePackId, setActivePackId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editingChapterId, setEditingChapterId] = useState(null);
  const [showNewChapter, setShowNewChapter] = useState(false);
  const [showNewPart, setShowNewPart] = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [showNewPerson, setShowNewPerson] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ch, ev, pp, ss] = await Promise.all([
        lifeStoryApi.get(projectId),
        lifeStoryApi.listChapters(projectId, { limit: 500 }),
        timelineApi.list(projectId, { limit: 500 }),
        peopleApi.list(projectId, { limit: 500 }),
        interviewApi.listSessions(projectId),
      ]);
      setOverview(ov);
      setChapters(ch.items || []);
      setEvents(ev.items || []);
      setPeople(pp.items || []);
      setSessions(ss || []);
    } catch (e) {
      toast.error(`Failed to load life story: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center" data-testid="life-story-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-12 text-center">
        <h2 className="text-xl">Life story not found</h2>
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mt-4">Back</Button>
      </div>
    );
  }

  const project = overview.project;
  const structure = overview.structure || {};
  const parts = structure.parts || [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="life-story-studio-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-gradient-to-r from-purple-950/40 via-slate-900 to-slate-900 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="life-story-back-btn" className="shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-purple-400 shrink-0" />
                <h1 className="text-base sm:text-xl font-bold tracking-tight truncate" data-testid="life-story-title">{project.title}</h1>
              </div>
              <p className="text-xs text-slate-400 truncate">
                {MODE_LABELS[structure.mode] || "Memoir"}
                {structure.subject_name ? ` · ${structure.subject_name}` : ""}
                {` · ${overview.chapter_count} chapter${overview.chapter_count === 1 ? "" : "s"}`}
                <span className="hidden sm:inline">
                  {` · ${overview.timeline_count} event${overview.timeline_count === 1 ? "" : "s"}`}
                  {` · ${overview.people_count} ${overview.people_count === 1 ? "person" : "people"}`}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <Tabs defaultValue="outline" data-testid="life-story-tabs">
          <TabsList className="bg-slate-900 border border-slate-800 w-full justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="outline" data-testid="tab-outline" className="shrink-0"><BookOpen className="w-4 h-4 mr-1 sm:mr-2" /> Outline</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline" className="shrink-0"><Clock className="w-4 h-4 mr-1 sm:mr-2" /> Timeline</TabsTrigger>
            <TabsTrigger value="people" data-testid="tab-people" className="shrink-0"><Users className="w-4 h-4 mr-1 sm:mr-2" /> People</TabsTrigger>
            <TabsTrigger value="interview" data-testid="tab-interview" className="shrink-0"><MessageSquare className="w-4 h-4 mr-1 sm:mr-2" /> Interview</TabsTrigger>
          </TabsList>

          {/* ========== OUTLINE ========== */}
          <TabsContent value="outline" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-6">
              <Dialog open={showNewPart} onOpenChange={setShowNewPart}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-500/40 text-purple-300" data-testid="ls-new-part-btn">
                    <Plus className="w-4 h-4 mr-2" /> New part
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>Add a part</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">
                      Top-level grouping (e.g. Childhood, Adulthood, Legacy).
                    </DialogDescription>
                  </DialogHeader>
                  <SimpleAddForm
                    placeholder="Part I — Childhood"
                    buttonText="Add part"
                    onSubmit={async (title) => {
                      await lifeStoryApi.addPart(projectId, { title });
                      setShowNewPart(false);
                      refresh();
                    }}
                  />
                </DialogContent>
              </Dialog>

              {parts.length > 0 && (
                <Dialog open={showNewChapter} onOpenChange={setShowNewChapter}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-purple-500 to-fuchsia-600 hover:from-purple-400" data-testid="ls-new-chapter-btn">
                      <BookOpen className="w-4 h-4 mr-2" /> New chapter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add a chapter</DialogTitle>
                      <DialogDescription className="text-slate-400 text-xs">Quick draft. Edit fully after creation.</DialogDescription>
                    </DialogHeader>
                    <ChapterForm
                      parts={parts}
                      onSubmit={async (payload) => {
                        try {
                          await lifeStoryApi.createChapter(projectId, payload);
                          toast.success("Chapter added");
                          setShowNewChapter(false);
                          refresh();
                        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                      }}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {parts.length === 0 && chapters.length === 0 && (
              <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="ls-outline-empty">
                <BookOpen className="w-12 h-12 mx-auto text-purple-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-300">Your story has no parts yet</h3>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  Create parts like &quot;Childhood&quot;, &quot;Adulthood&quot;, &quot;Legacy&quot; — then add chapters inside.
                </p>
              </div>
            )}

            <div className="space-y-6">
              {parts.map((part) => {
                const partChapters = chapters.filter((c) => c.part_id === part.id);
                return (
                  <div key={part.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-5" data-testid={`ls-part-${part.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-lg font-semibold text-purple-300">{part.title}</h2>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-400"
                              onClick={async () => {
                                if (window.confirm("Delete this part? Chapters stay but unbinned.")) {
                                  await lifeStoryApi.removePart(projectId, part.id); refresh();
                                }
                              }}
                              data-testid={`ls-part-delete-${part.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {partChapters.length === 0 ? (
                      <p className="text-xs text-slate-600 italic">No chapters here yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`ls-part-chapters-${part.id}`}>
                        {partChapters.map((c) => (
                          <ChapterCard key={c.id} chapter={c} onClick={() => setEditingChapterId(c.id)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {(() => {
                const unbinned = chapters.filter((c) => !c.part_id);
                return unbinned.length > 0 ? (
                  <div className="bg-slate-900/30 border border-dashed border-slate-700 rounded-lg p-5">
                    <h3 className="text-sm font-medium text-slate-400 mb-3">Unbinned chapters</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {unbinned.map((c) => <ChapterCard key={c.id} chapter={c} onClick={() => setEditingChapterId(c.id)} />)}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </TabsContent>

          {/* ========== TIMELINE ========== */}
          <TabsContent value="timeline" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
              <h2 className="text-lg font-semibold text-purple-300">Timeline of life events</h2>
              <Dialog open={showNewEvent} onOpenChange={setShowNewEvent}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-500" data-testid="ls-new-event-btn">
                    <Plus className="w-4 h-4 mr-2" /> New event
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>Add a timeline event</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">Dated milestone or memory.</DialogDescription>
                  </DialogHeader>
                  <EventForm people={people} onSubmit={async (payload) => {
                    try {
                      await timelineApi.create(projectId, payload);
                      toast.success("Event added");
                      setShowNewEvent(false);
                      refresh();
                    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                  }} />
                </DialogContent>
              </Dialog>
            </div>

            {events.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="ls-timeline-empty">
                <Clock className="w-12 h-12 mx-auto text-purple-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-300">No timeline events yet</h3>
                <p className="text-sm text-slate-500 mt-2">Add births, marriages, milestones, defining moments.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="ls-timeline-list">
                {events.map((e) => (
                  <div key={e.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex flex-col sm:flex-row gap-2 sm:gap-4" data-testid={`event-row-${e.id}`}>
                    <div className="flex-shrink-0 sm:w-24 text-purple-300 text-sm font-mono">
                      {e.event_date || e.date_text || "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold">{e.title}</h3>
                      {e.description && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{e.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-slate-500">
                        {e.location && <span>📍 {e.location}</span>}
                        {(e.related_people || []).slice(0, 3).map((pid) => {
                          const pp = people.find((x) => x.id === pid);
                          return pp ? <Badge key={pid} variant="outline" className="text-[10px] border-slate-700">{pp.name}</Badge> : null;
                        })}
                        {e.source_vault_item_id && <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">from vault</Badge>}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-400 self-start sm:self-auto"
                            onClick={async () => { if (window.confirm("Delete this event?")) { await timelineApi.remove(projectId, e.id); refresh(); }}}
                            data-testid={`event-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== PEOPLE ========== */}
          <TabsContent value="people" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
              <h2 className="text-lg font-semibold text-purple-300">People in this story</h2>
              <Dialog open={showNewPerson} onOpenChange={setShowNewPerson}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-500" data-testid="ls-new-person-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add person
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>Add a person</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">Family member, friend, or anyone meaningful.</DialogDescription>
                  </DialogHeader>
                  <PersonForm onSubmit={async (payload) => {
                    try {
                      await peopleApi.create(projectId, payload);
                      toast.success("Person added");
                      setShowNewPerson(false);
                      refresh();
                    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                  }} />
                </DialogContent>
              </Dialog>
            </div>

            {people.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="ls-people-empty">
                <Users className="w-12 h-12 mx-auto text-purple-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-300">No people yet</h3>
                <p className="text-sm text-slate-500 mt-2">Capture family, friends, mentors, ancestors.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ls-people-grid">
                {people.map((p) => (
                  <div key={p.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`person-card-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-100 truncate">{p.name}</h3>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {p.relationship ? `${p.relationship}` : ""}
                          {p.birth_date || p.death_date ? ` · ${p.birth_date || "?"} – ${p.death_date || "present"}` : ""}
                        </p>
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-red-400"
                              onClick={async () => { if (window.confirm("Remove this person?")) { await peopleApi.remove(projectId, p.id); refresh(); } }}
                              data-testid={`person-delete-${p.id}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    {p.biography_notes && <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.biography_notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== INTERVIEW ========== */}
          <TabsContent value="interview" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
              <h2 className="text-lg font-semibold text-purple-300">Interview Mode</h2>
              <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-500" data-testid="ls-new-session-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> Start interview
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>Start an interview</DialogTitle>
                    <DialogDescription className="text-slate-400 text-xs">
                      Pick a curated question pack. Answers can become vault items, timeline events, or chapters.
                    </DialogDescription>
                  </DialogHeader>
                  <NewSessionForm
                    onCreated={(s) => { setShowNewSession(false); setActivePackId(s.id); refresh(); }}
                    projectId={projectId}
                  />
                </DialogContent>
              </Dialog>
            </div>

            {sessions.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="ls-interview-empty">
                <MessageSquare className="w-12 h-12 mx-auto text-purple-700 mb-4" />
                <h3 className="text-lg font-semibold text-slate-300">No interview sessions yet</h3>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  Curated questions to help capture memories about childhood, family, career, milestones, and legacy.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map((s) => (
                  <div key={s.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`session-row-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{s.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{s.pack_id} · interviewee: {s.interviewee_name || "—"}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setActivePackId(s.id)} data-testid={`session-open-${s.id}`}>
                        Open <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Chapter editor */}
      {editingChapterId && (
        <Dialog open onOpenChange={() => setEditingChapterId(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit chapter</DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">Refine title, body, and metadata.</DialogDescription>
            </DialogHeader>
            <ChapterEditor projectId={projectId} chapterId={editingChapterId} parts={parts}
              onSaved={() => { setEditingChapterId(null); refresh(); }}
              onDeleted={() => { setEditingChapterId(null); refresh(); }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Interview session detail */}
      {activePackId && (
        <Dialog open onOpenChange={() => setActivePackId(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>Interview session</DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">Answer questions in any order. Promote answers to chapters or timeline events.</DialogDescription>
            </DialogHeader>
            <SessionDetail projectId={projectId} sessionId={activePackId} parts={parts}
              onChanged={refresh}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- subcomponents ----------

function ChapterCard({ chapter, onClick }) {
  return (
    <div onClick={onClick}
         className="bg-slate-900 border border-slate-800 rounded-lg p-4 hover:border-purple-500/40 transition-colors cursor-pointer"
         data-testid={`chapter-card-${chapter.id}`}>
      <h4 className="font-semibold text-slate-100 line-clamp-2">{chapter.title}</h4>
      {chapter.summary && <p className="text-xs text-slate-500 line-clamp-2 mt-1">{chapter.summary}</p>}
      <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500">
        <span>{chapter.word_count} words</span>
        {chapter.status === "submitted" && <Badge variant="outline" className="text-[10px] border-amber-700 text-amber-400">submitted</Badge>}
        {chapter.source_interview_answer_id && <Badge variant="outline" className="text-[10px] border-purple-700 text-purple-300">from interview</Badge>}
        {chapter.source_vault_item_id && <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">from vault</Badge>}
      </div>
    </div>
  );
}

function SimpleAddForm({ placeholder, buttonText, onSubmit, inline = false }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className={inline ? "flex gap-2" : "space-y-3"}>
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder}
             onKeyDown={(e) => e.key === "Enter" && value.trim() && onSubmit(value.trim())} />
      <Button onClick={async () => { setBusy(true); try { await onSubmit(value.trim()); } finally { setBusy(false); } }}
              disabled={busy || !value.trim()} className={inline ? "" : "w-full"}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} {buttonText}
      </Button>
    </div>
  );
}

function ChapterForm({ parts, initial = {}, onSubmit }) {
  const [title, setTitle] = useState(initial.title || "");
  const [summary, setSummary] = useState(initial.summary || "");
  const [body, setBody] = useState(initial.body || "");
  const [partId, setPartId] = useState(initial.part_id || (parts[0]?.id || ""));
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Chapter 1 — Earliest Memories" data-testid="chapter-form-title" />
      </div>
      <div><Label className="text-xs">Summary</Label>
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} data-testid="chapter-form-summary" />
      </div>
      <div><Label className="text-xs">Part</Label>
        <Select value={partId} onValueChange={setPartId}>
          <SelectTrigger data-testid="chapter-form-part"><SelectValue placeholder="Pick a part" /></SelectTrigger>
          <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Body</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} placeholder="Write your chapter here…" data-testid="chapter-form-body" />
      </div>
      <Button onClick={async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        setBusy(true);
        try { await onSubmit({ title, summary, body, part_id: partId || null }); } finally { setBusy(false); }
      }} disabled={busy || !title.trim()} className="w-full" data-testid="chapter-form-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />} Save chapter
      </Button>
    </div>
  );
}

function ChapterEditor({ projectId, chapterId, parts, onSaved, onDeleted }) {
  const [chapter, setChapter] = useState(null);
  useEffect(() => { lifeStoryApi.getChapter(projectId, chapterId).then(setChapter).catch(() => setChapter(null)); }, [projectId, chapterId]);
  if (!chapter) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8 text-slate-500" />;
  return (
    <div className="space-y-4">
      <ChapterForm parts={parts} initial={chapter} onSubmit={async (payload) => {
        await lifeStoryApi.updateChapter(projectId, chapterId, payload);
        toast.success("Saved");
        onSaved();
      }} />
      <Button variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={async () => {
                if (window.confirm("Delete this chapter?")) {
                  await lifeStoryApi.deleteChapter(projectId, chapterId); onDeleted();
                }
              }}>
        <Trash2 className="w-4 h-4 mr-2" /> Delete chapter
      </Button>
    </div>
  );
}

function EventForm({ people, onSubmit }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [personId, setPersonId] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="event-form-title" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</Label>
          <Input value={date} onChange={(e) => setDate(e.target.value)} placeholder="1985-04-12 or 1985 or circa 1985" data-testid="event-form-date" />
        </div>
        <div><Label className="text-xs">Location</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="event-form-location" />
        </div>
      </div>
      <div><Label className="text-xs">Related person (optional)</Label>
        <Select value={personId || "_none"} onValueChange={(v) => setPersonId(v === "_none" ? "" : v)}>
          <SelectTrigger data-testid="event-form-person"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— none —</SelectItem>
            {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="event-form-description" />
      </div>
      <Button onClick={async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        setBusy(true);
        try {
          // Date heuristic: 4-digit numeric → ISO; otherwise → date_text
          const isISO = /^\d{4}(-\d{2}(-\d{2})?)?$/.test(date.trim());
          await onSubmit({
            title, description, location,
            event_date: isISO ? date.trim() : null,
            date_text: isISO ? "" : date.trim(),
            related_people: personId ? [personId] : [],
          });
        } finally { setBusy(false); }
      }} disabled={busy || !title.trim()} className="w-full" data-testid="event-form-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />} Save event
      </Button>
    </div>
  );
}

function PersonForm({ onSubmit }) {
  const [data, setData] = useState({ name: "", relationship: "", role: "family", birth_date: "", death_date: "", biography_notes: "" });
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Name</Label>
        <Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} data-testid="person-form-name" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">Relationship</Label>
          <Input value={data.relationship} onChange={(e) => setData({ ...data, relationship: e.target.value })} placeholder="mother, friend, mentor" data-testid="person-form-relationship" />
        </div>
        <div><Label className="text-xs">Role</Label>
          <Select value={data.role} onValueChange={(v) => setData({ ...data, role: v })}>
            <SelectTrigger data-testid="person-form-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="subject">Subject</SelectItem>
              <SelectItem value="family">Family</SelectItem>
              <SelectItem value="friend">Friend</SelectItem>
              <SelectItem value="colleague">Colleague</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">Birth</Label>
          <Input value={data.birth_date} onChange={(e) => setData({ ...data, birth_date: e.target.value })} placeholder="1923" data-testid="person-form-birth" />
        </div>
        <div><Label className="text-xs">Death</Label>
          <Input value={data.death_date} onChange={(e) => setData({ ...data, death_date: e.target.value })} placeholder="2010 (leave empty if living)" data-testid="person-form-death" />
        </div>
      </div>
      <div><Label className="text-xs">Notes</Label>
        <Textarea value={data.biography_notes} onChange={(e) => setData({ ...data, biography_notes: e.target.value })} rows={3} data-testid="person-form-notes" />
      </div>
      <Button onClick={async () => {
        if (!data.name.trim()) { toast.error("Name required"); return; }
        setBusy(true);
        try { await onSubmit(data); } finally { setBusy(false); }
      }} disabled={busy || !data.name.trim()} className="w-full" data-testid="person-form-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add person
      </Button>
    </div>
  );
}

function NewSessionForm({ onCreated, projectId }) {
  const [packs, setPacks] = useState([]);
  const [packId, setPackId] = useState("");
  const [interviewee, setInterviewee] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { interviewApi.packs().then((r) => { setPacks(r.packs || []); if (r.packs?.[0]) setPackId(r.packs[0].id); }); }, []);
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Question pack</Label>
        <Select value={packId} onValueChange={setPackId}>
          <SelectTrigger data-testid="session-form-pack"><SelectValue /></SelectTrigger>
          <SelectContent>
            {packs.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.display_name} · {p.question_count} questions</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {packs.find((p) => p.id === packId) && <p className="text-xs text-slate-500 mt-1">{packs.find((p) => p.id === packId).description}</p>}
      </div>
      <div><Label className="text-xs">Interviewee name (optional)</Label>
        <Input value={interviewee} onChange={(e) => setInterviewee(e.target.value)} data-testid="session-form-interviewee" />
      </div>
      <Button onClick={async () => {
        setBusy(true);
        try {
          const s = await interviewApi.createSession(projectId, { pack_id: packId, interviewee_name: interviewee });
          toast.success("Session started");
          onCreated(s);
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
        finally { setBusy(false); }
      }} disabled={busy || !packId} className="w-full" data-testid="session-form-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Start session
      </Button>
    </div>
  );
}

function SessionDetail({ projectId, sessionId, parts, onChanged }) {
  const [data, setData] = useState(null);
  const [pack, setPack] = useState(null);
  const [savingMap, setSavingMap] = useState({});

  useEffect(() => {
    interviewApi.getSession(projectId, sessionId).then(async (d) => {
      setData(d);
      const p = await interviewApi.pack(d.session.pack_id);
      setPack(p);
    });
  }, [projectId, sessionId]);

  const answersByQ = useMemo(() => {
    const m = {};
    (data?.answers || []).forEach((a) => { m[a.question_id] = a; });
    return m;
  }, [data]);

  const reload = async () => {
    const d = await interviewApi.getSession(projectId, sessionId);
    setData(d);
    onChanged && onChanged();
  };

  const saveAnswer = async (q, text) => {
    setSavingMap((s) => ({ ...s, [q.id]: true }));
    try {
      await interviewApi.upsertAnswer(projectId, sessionId, {
        question_id: q.id, question_prompt: q.prompt, answer_text: text,
      });
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSavingMap((s) => ({ ...s, [q.id]: false })); }
  };

  const promote = async (answer, target, partId = null) => {
    try {
      await interviewApi.promote(projectId, answer.id, { target, part_id: partId });
      toast.success(`Promoted to ${target.replace("_", " ")}`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (!data || !pack) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8 text-slate-500" />;

  return (
    <div className="space-y-6">
      <div className="text-sm text-slate-400">{pack.description}</div>
      {pack.questions.map((q) => {
        const ans = answersByQ[q.id];
        return (
          <QuestionRow key={q.id} q={q} existing={ans}
            saving={savingMap[q.id]} onSave={(text) => saveAnswer(q, text)}
            onPromote={(target, partId) => promote(ans, target, partId)}
            parts={parts}
          />
        );
      })}
    </div>
  );
}

function QuestionRow({ q, existing, saving, onSave, onPromote, parts }) {
  const [text, setText] = useState(existing?.answer_text || "");
  useEffect(() => { setText(existing?.answer_text || ""); }, [existing?.id, existing?.answer_text]);
  const dirty = text !== (existing?.answer_text || "");
  return (
    <div className="border border-slate-800 rounded-lg p-4 space-y-2" data-testid={`q-${q.id}`}>
      <p className="text-sm font-medium text-purple-200">{q.prompt}</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                placeholder="Type your answer here…"
                data-testid={`q-textarea-${q.id}`} />
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <Button size="sm" onClick={() => onSave(text)} disabled={saving || !dirty} data-testid={`q-save-${q.id}`}>
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          {existing ? "Update" : "Save"}
        </Button>
        {existing && (
          <div className="flex gap-1 text-xs">
            <Button size="sm" variant="outline" onClick={() => onPromote("vault_item")} data-testid={`q-promote-vault-${q.id}`}>
              → Vault
            </Button>
            <Button size="sm" variant="outline" onClick={() => onPromote("timeline_event")} data-testid={`q-promote-timeline-${q.id}`}>
              → Timeline
            </Button>
            <Button size="sm" variant="outline" onClick={() => onPromote("life_story_chapter", parts[0]?.id || null)} data-testid={`q-promote-chapter-${q.id}`}>
              → Chapter
            </Button>
          </div>
        )}
      </div>
      {existing?.derived_items?.length > 0 && (
        <p className="text-[10px] text-slate-500">
          Promoted to: {existing.derived_items.map((d) => d.entity_type).join(", ")}
        </p>
      )}
    </div>
  );
}
